import { SvodProvider, ThirdPartyProvider } from './../../me/providers/types';
import moment from 'moment';
import { canOnlyBeBoughtWithMoneyChannels } from '../../../../config';
import { getLowestPrice } from '../../../../utils/ats-details-utils';
import { getAuth } from '../../../../utils/auth-utils';
import { invariant, lowercase, toError, warn } from '../../../../utils/commonUtils';
import { getTimeStatus, isLimitedToHomeNetwork } from '../../../../views/ats-details/details-call-to-action/utils';
import { SourceParsingError } from '../../../../views/ats-details/details-errors';
import { InputContext } from '../../../../views/ats-details/use-input-context';
import { Bookmark } from '../../me/bookmarks/types';
import { SubscribedProduct } from '../../me/products/types';
import { ProgramProvider, Provider, VodProvider } from '../../me/providers/types';
import { Recording } from '../../recordings/types/recording';
import { VodPlaybackOption, ProgramPlaybackOption, PlayablePlaybackOption } from '../types/playback-option';
import {
  BookmarkedSource,
  ProgramSource,
  RerunProgram,
  SeriesSource,
  Source,
  SourceProduct,
  SvodSource,
  ThirdPartySource,
  VodSource,
} from '../types/sources';
import { EpisodeTitle, PlayableTitle, SeriesTitle, Title } from '../types/title';
import { findRecordings, getPreferredRecording } from './recording-utils';
import { routes } from '../../../../routes/route-utils';
import { Language } from '../types/shared';
import { createThirdPartyDeeplink } from '../../common';

export function isBookmarkedSource(source: Source): source is BookmarkedSource {
  return source.bookmark !== undefined;
}

export function sourcesFromSeries(title: SeriesTitle, providers: Provider[]) {
  const sources: SeriesSource[] = [];

  for (const option of title.playbackOptions) {
    const provider = providers.find((p) => p.id === option.providerIdentifier);

    if (!provider) {
      warn(`Provider for "${option.providerIdentifier}" was not found.`);
      continue;
    }

    sources.push({
      type: lowercase(provider.type),
      id: provider.id,
      name: provider.name,
      image: provider.image,
      isSubscribed: provider.isSubscribed,
    });
  }

  return sources;
}

export function findEpisodePlaybackOptions(title: Title, episodes: EpisodeTitle[]) {
  const options = new Map<string, PlayablePlaybackOption>();

  for (const episode of episodes) {
    for (const option of episode.playbackOptions) {
      options.set(option.providerIdentifier, option);
    }
  }

  invariant(
    options.size > 0,
    `No playback options found for episodes on title "${title.titleName} (${title.masterId})".`,
  );

  return Array.from(options.values());
}

export function createSources(
  options: PlayableTitle['playbackOptions'],
  providers: Provider[],
  recordings: Recording[],
  products: SubscribedProduct[],
  bookmarks: Bookmark[],
) {
  let sources: Source[] = [];
  const errors = new Set<Error>();

  const reruns = getReruns(options, providers);

  for (const option of options) {
    try {
      const source = createSource(option, reruns, providers, recordings, products, bookmarks);

      if (source) {
        sources.push(source);
      }
    } catch (err) {
      errors.add(toError(err));
    }
  }

  if (errors.size > 0) {
    throw new SourceParsingError(`Failed to create sources.\n${Array.from(errors).join('\n')}`);
  }

  if (sources.length === 0) {
    throw new SourceParsingError('No sources created.');
  }

  return sources;
}

function createSource(
  option: PlayablePlaybackOption,
  reruns: RerunProgram[],
  providers: Provider[],
  recordings: Recording[],
  products: SubscribedProduct[],
  bookmarks: Bookmark[],
): Source | undefined {
  const provider = providers.find((p) => p.id === option.providerIdentifier);
  const bookmark = bookmarks.find((b) => b.platformTitleId === option.platformTitleId);

  if (!provider) {
    warn(`Provider for "${option.providerIdentifier}" was not found.`);
    return undefined;
  }

  const type = lowercase(provider.type);
  if (type === 'program') {
    invariant(provider.type === 'program', `Expected provider type to be "channel", but got "${provider.type}".`);
    invariant(
      option.serviceType === 'program',
      `Expected option service type to be "tv", but got "${option.serviceType}".`,
    );
    return createProgramSource(option, reruns, provider, recordings, bookmark);
  }

  if (type === 'vod') {
    invariant(provider.type === 'vod', `Expected provider type to be "vod", but got "${provider.type}".`);
    invariant(option.serviceType === 'vod', `Expected option service type to not be "program"`);
    return createVodSource(option, provider, products, bookmark);
  }

  invariant(option.serviceType !== 'program', `Expected option service type to not be "program"`);

  if (type === 'svod') {
    invariant(provider.type === 'svod', `Expected provider type to be "svod", but got "${provider.type}".`);
    return createSvodSource(option, provider as SvodProvider, bookmark);
  }

  return {
    type,
    id: provider.id,
    name: provider.name,
    image: provider.image,
    platformTitleId: option.platformTitleId,
    providerTitleId: option.providerTitleId,
    providerIdentifier: option.providerIdentifier,
    distributor: option.distributor,
    languages: createLanguages(option.subtitleLanguages, option.audioLanguages),
    links: {
      open: option.links.web?.open || undefined,
      play: option.links.web?.play || undefined,
    },
    isSubscribed: provider.isSubscribed,
    bookmark: bookmark,
    hasTrailer: option.hasTrailer,
    popupTextType: 'params' in provider ? provider.params.popupTextType : undefined,
    availableFrom: option.availableFrom,
    availableTo: option.availableTo,
  };
}

function getSourceProduct(products: SubscribedProduct[], option: VodPlaybackOption): SourceProduct {
  const product = products.find((p) => p.platformTitleId === option.platformTitleId);

  if (product === undefined) {
    return {
      status: 'pending',
      price: getLowestPrice(option.price) ?? option.price[0],
    };
  }

  if (product.type === 'purchase') {
    return {
      status: 'purchased',
    };
  }

  if (product.type === 'rental') {
    return {
      status: 'rented',
      expiresAt: product.expiresAt,
    };
  }

  return {
    status: 'pending',
    price: getLowestPrice(option.price) ?? option.price[0],
  };
}

function createSvodSource(
  option: PlayablePlaybackOption,
  provider: SvodProvider,
  bookmark: Bookmark | undefined,
): SvodSource {
  return {
    type: 'svod',
    id: provider.id,
    name: provider.name,
    image: provider.image,
    platformTitleId: option.platformTitleId,
    providerTitleId: option.providerTitleId,
    providerIdentifier: option.providerIdentifier,
    distributor: option.distributor,
    languages: createLanguages(option.subtitleLanguages, option.audioLanguages),
    links: {
      open: option.links.web?.open || undefined,
      play: option.links.web?.play || undefined,
    },
    isSubscribed: provider.isSubscribed,
    bookmark: bookmark,
    hasTrailer: option.hasTrailer,
    popupTextType: provider.params.popupTextType,
  };
}

function createVodSource(
  option: VodPlaybackOption,
  provider: VodProvider,
  products: SubscribedProduct[],
  bookmark: Bookmark | undefined,
): Source {
  return {
    type: 'vod',
    id: provider.id,
    name: provider.name,
    image: provider.image,
    platformTitleId: option.platformTitleId,
    providerTitleId: option.providerTitleId,
    providerIdentifier: option.providerIdentifier,
    distributor: option.distributor,
    languages: createLanguages(option.subtitleLanguages, option.audioLanguages),
    isSubscribed: provider.isSubscribed,
    product: getSourceProduct(products, option),
    bookmark: bookmark,
    hasTrailer: option.hasTrailer,
  };
}

function createProgramSource(
  option: ProgramPlaybackOption,
  reruns: RerunProgram[],
  provider: ProgramProvider,
  recordings: Recording[],
  bookmark: Bookmark | undefined,
): ProgramSource {
  const recs = findRecordings(recordings, option);

  return {
    type: 'program',
    id: option.channelId,
    name: provider.name,
    image: provider.image,
    isSubscribed: provider.isSubscribed,
    platformTitleId: option.platformTitleId,
    providerTitleId: option.providerTitleId,
    providerIdentifier: option.providerIdentifier,
    distributor: option.distributor,
    languages: createLanguages(option.subtitleLanguages, option.audioLanguages),
    channelId: option.channelId,
    channelName: option.channelName,
    channelNumber: provider.channelNumber,
    seriesId: option.seriesId,
    lifetimeId: option.lifetimeId,
    startTime: option.startTime,
    endTime: option.endTime,
    availableFrom: option.availableFrom,
    expiresAt: option.availableTo,
    permissions: {
      supportsLiveTV: provider.supportsLiveTV,
      supportsCatchup: option.supportsCatchup,
      supportsRecording: provider.isSubscribed,
      isLimitedToHomeNetwork: provider.isLimitedToHomeNetwork,
      canOnlyBeBoughtWithMoney: canOnlyBeBoughtWithMoneyChannels.includes(option.channelId),
    },
    relatedSource:
      provider.relatedProvider !== undefined
        ? createRelatedProviderSource(option, provider.relatedProvider)
        : undefined,
    recordings: recs,
    bookmark: bookmark,
    reruns: reruns,
    hasTrailer: option.hasTrailer,
  };
}

function createRelatedProviderSource(
  options: ProgramPlaybackOption,
  provider: ThirdPartyProvider | SvodProvider,
): ThirdPartySource | SvodSource {
  if (provider.type === 'thirdParty') {
    return createRelatedThirdPartyProgramSource(options, provider);
  }

  return createRelatedSvodSource(options, provider);
}

function createRelatedSvodSource(option: ProgramPlaybackOption, provider: SvodProvider): SvodSource {
  return {
    type: 'svod',
    id: provider.id,
    name: provider.name,
    image: provider.image,
    platformTitleId: option.platformTitleId,
    providerTitleId: option.providerTitleId,
    providerIdentifier: option.providerIdentifier,
    distributor: option.distributor,
    languages: createLanguages(option.subtitleLanguages, option.audioLanguages),
    bookmark: undefined,
    isSubscribed: provider.isSubscribed,
    hasTrailer: option.hasTrailer,
    popupTextType: provider.params.popupTextType,
    links: {
      play: routes
        .play({ platformTitleId: option.platformTitleId, providerIdentifier: option.providerIdentifier })
        .toString(),
      open: undefined,
    },
  };
}

function createRelatedThirdPartyProgramSource(
  option: ProgramPlaybackOption,
  provider: ThirdPartyProvider,
): ThirdPartySource {
  const baseSource = {
    type: 'thirdparty',
    id: provider.id,
    name: provider.name,
    image: provider.image,
    platformTitleId: option.platformTitleId,
    providerTitleId: option.providerTitleId,
    providerIdentifier: option.providerIdentifier,
    distributor: option.distributor,
    languages: createLanguages(option.subtitleLanguages, option.audioLanguages),
    isSubscribed: provider.isSubscribed,
    bookmark: undefined,
    hasTrailer: option.hasTrailer,
    popupTextType: provider.params.popupTextType,
    startTime: option.startTime,
    endTime: option.endTime,
    availableFrom: option.availableFrom,
    availableTo: option.availableTo,
  } as const;

  if (option.links.web === null) {
    return {
      ...baseSource,
      links: {
        play: undefined,
        open: undefined,
      },
    };
  }

  if (option.links.web.play === null) {
    return {
      ...baseSource,
      links: {
        play: undefined,
        open: undefined,
      },
    };
  }

  if (option.links.web.play.length === 0) {
    return {
      ...baseSource,
      links: {
        play: undefined,
        open: undefined,
      },
    };
  }

  const url = createThirdPartyDeeplink(provider, option.links.web.play);

  return {
    ...baseSource,
    links: {
      play: url.toString(),
      open: undefined,
    },
  };
}

export function getReruns(options: PlayablePlaybackOption[], providers: Provider[]): RerunProgram[] {
  const now = moment();

  // Re-runs sorted by best availability
  return options
    .filter(
      (po): po is ProgramPlaybackOption =>
        po.serviceType === 'program' &&
        po.supportsCatchup &&
        now.isAfter(po.availableFrom) &&
        now.isBefore(po.availableTo),
    )
    .map((po) => {
      const optionProvider = providers.find(
        (x) => x.type === 'program' && x.id === po.providerIdentifier,
      ) as ProgramProvider;

      return {
        id: po.id,
        platformTitleId: po.platformTitleId,
        providerIdentifier: po.providerIdentifier,
        providerName: po.providerName,
        availableFrom: po.availableFrom,
        expiresAt: po.availableTo,
        sourceId: po.sourceId,
        relatedSource:
          optionProvider !== undefined && optionProvider.relatedProvider !== undefined
            ? createRelatedProviderSource(po, optionProvider.relatedProvider)
            : undefined,
      };
    })
    .sort((a, b) => b.expiresAt.localeCompare(a.expiresAt));
}

export function isLive(program: Pick<ProgramSource, 'startTime' | 'endTime'>) {
  return moment().isBetween(program.startTime, program.endTime);
}

export function isPlayable(source: Source) {
  switch (source.type) {
    case 'program':
      return isProgramPlayable(source);
    case 'vod':
      const isOwned = source.product.status === 'purchased' || source.product.status === 'rented';
      const isExpired = source.product.status === 'rented' && moment().isAfter(source.product.expiresAt);
      return isOwned && !isExpired;
    case 'svod':
      return source.isSubscribed;
    case 'thirdparty':
      return source.isSubscribed;
    default:
      // @ts-expect-error
      throw Error(`Unknown source type "${source.type}"`);
  }
}

function isProgramPlayable(source: ProgramSource) {
  const time = getTimeStatus(source);
  const auth = getAuth();

  switch (time) {
    case 'present':
      if (isLimitedToHomeNetwork(source) && !auth.isHome) {
        return false;
      }

      return source.permissions.supportsLiveTV;
    case 'past':
      return source.permissions.supportsCatchup && moment().isBefore(source.expiresAt);
    case 'future':
      return false;
    default:
      return false;
  }
}

function createLanguages(subtitleLanguages: Language[], audioLanguages: Language[]) {
  const subtitles = subtitleLanguages.map(
    (lang) =>
      ({
        ...lang,
        type: 'subtitle',
      } as const),
  );

  const audio = audioLanguages.map(
    (lang) =>
      ({
        ...lang,
        type: 'audio',
      } as const),
  );

  return [...subtitles, ...audio];
}

export class Sources<T extends Source[]> {
  constructor(private sources: T) {}

  public get size() {
    return this.sources.length;
  }

  public get isEmpty() {
    return this.size === 0;
  }

  public get first() {
    return this.sources[0];
  }

  public get last() {
    return this.sources[this.size - 1];
  }

  public programs(): Sources<ProgramSource[]> {
    const filtered = this.sources.filter((source): source is ProgramSource => source.type === 'program');
    return new Sources(filtered);
  }

  public vod(): Sources<VodSource[]> {
    const filtered = this.sources.filter((source): source is VodSource => source.type === 'vod');
    return new Sources(filtered);
  }

  public svod(): Sources<SvodSource[]> {
    const filtered = this.sources.filter((source): source is SvodSource => source.type === 'svod');
    return new Sources(filtered);
  }

  public thirdparty(): Sources<ThirdPartySource[]> {
    const filtered = this.sources.filter((source): source is ThirdPartySource => source.type === 'thirdparty');
    return new Sources(filtered);
  }

  public recorded() {
    const filtered = this.sources.filter((source) => {
      if (source.type !== 'program') {
        return false;
      }

      const recording = getPreferredRecording(source, true);
      return recording !== undefined && recording.status === 'completed';
    }) as ProgramSource[];

    const diff = (source: ProgramSource) => {
      const recording = getPreferredRecording(source, true);

      if (recording === undefined) {
        return 0;
      }

      return Math.abs(moment().diff(recording.startTime));
    };

    const sorted = filtered.sort((a, b) => diff(a) - diff(b));

    return new Sources<ProgramSource[]>(sorted);
  }

  public bookmarked() {
    type BookmarkedSource = T[number] & { bookmark: Bookmark };
    const filtered = this.sources.filter(
      (source) => source.bookmark !== undefined && !source.bookmark.isWatched,
    ) as BookmarkedSource[];
    return new Sources<BookmarkedSource[]>(filtered);
  }

  public playable() {
    const filtered = this.sources.filter((source) => isPlayable(source)) as T;
    return new Sources<T>(filtered);
  }

  public live() {
    const filtered = this.sources.filter(
      (source): source is ProgramSource => source.type === 'program' && isLive(source),
    );
    return new Sources<ProgramSource[]>(filtered);
  }

  public subscribed() {
    const filtered = this.sources.filter((source) => source.isSubscribed) as T;
    return new Sources<T>(filtered);
  }

  public filter(fn: (source: T[number]) => boolean) {
    const filtered = this.sources.filter(fn);
    return new Sources(filtered as T);
  }

  public closest(fn: (obj: T[number]) => moment.MomentInput) {
    const now = moment();
    const sorted = [...this.sources].sort((a, b) => {
      const diffA = Math.abs(now.diff(moment(fn(a))));
      const diffB = Math.abs(now.diff(moment(fn(b))));
      return diffA - diffB;
    });
    return new Sources(sorted as T);
  }

  public byPlatformTitleId(platformTitleId: string) {
    const filtered = this.sources.filter((source) => source.platformTitleId === platformTitleId);
    return new Sources(filtered);
  }

  public byProviderId(providerId: string) {
    const filtered = this.sources.filter((source) => source.id === providerId);
    return new Sources(filtered);
  }

  public byType(type: Source['type']) {
    const filtered = this.sources.filter((source) => source.type === type);
    return new Sources(filtered);
  }

  public byInputContext(inputContext: InputContext) {
    let sources = new Sources<Source[]>(this.sources);

    if (inputContext.platformTitleId !== undefined) {
      sources = this.byPlatformTitleId(inputContext.platformTitleId);
    }

    if (inputContext.orderByProviderId !== undefined) {
      sources = sources.byProviderId(inputContext.orderByProviderId);
    }

    if (inputContext.orderByServiceType !== undefined) {
      sources = sources.byType(inputContext.orderByServiceType);
    }

    if (sources.isEmpty) {
      return new Sources<Source[]>(this.sources);
    }

    return sources;
  }

  public values() {
    return [...this.sources] as T;
  }
}
